import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "./lib/api";
import { SartellLogo } from "./components/Logo";
import { TeamSelect } from "./screens/TeamSelect";
import { LoginScreen } from "./screens/LoginScreen";
import { PlayerNameScreen } from "./screens/PlayerNameScreen";
import { ParentTracker } from "./screens/ParentTracker";
import { CoachView } from "./screens/CoachView";
import { AdminPanel } from "./screens/AdminPanel";
import { FamilyScreen } from "./screens/FamilyScreen";

/* ───────────────────── MAIN APP ───────────────────── */
const SESSION_KEY = "ssa_session";
const AUTHED_SCREENS = ["playerName", "tracker", "coach", "family"];

export default function AvailabilityApp() {
  // Restore a saved session on load — unless a magic link is present, which takes precedence.
  const [restored] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("t") && params.get("code")) return null;
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [screen, setScreen] = useState(restored?.screen || "teams");
  const [selectedTeam, setSelectedTeam] = useState(restored?.team || null);
  const [authData, setAuthData] = useState(
    restored ? { token: restored.token, role: restored.role, team: restored.team } : null
  );
  const [playerName, setPlayerName] = useState(restored?.playerName || "");
  const [family, setFamily] = useState(restored?.family || null);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicError, setMagicError] = useState("");

  /* Persist the session so a refresh / app-switch / return doesn't force re-login */
  useEffect(() => {
    if (authData && AUTHED_SCREENS.includes(screen)) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        token: authData.token,
        role: authData.role,
        team: authData.team,
        screen,
        playerName,
        family,
      }));
    } else if (!authData) {
      localStorage.removeItem(SESSION_KEY);
    }
  }, [authData, screen, playerName, family]);

  /* ── Browser-history navigation ──
     Apply a full nav snapshot (used on load and when the back button fires). */
  const applySnapshot = useCallback((s) => {
    setScreen(s.screen || "teams");
    setSelectedTeam(s.selectedTeam ?? null);
    setAuthData(s.authData ?? null);
    setPlayerName(s.playerName ?? "");
    setFamily(s.family ?? null);
  }, []);

  // Push a new history entry and move forward to the given snapshot.
  const pushNav = useCallback((s) => {
    applySnapshot(s);
    window.history.pushState({ nav: s }, "");
  }, [applySnapshot]);

  // Seed the initial history entry, and map the back button to the stored snapshot.
  useEffect(() => {
    window.history.replaceState(
      { nav: { screen, selectedTeam, authData, playerName, family } },
      ""
    );
    const onPop = (e) => {
      if (e.state && e.state.nav) {
        applySnapshot(e.state.nav);
      } else {
        applySnapshot({ screen: "teams" });
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /* Family link: one link covering every player this parent has, across teams.
     Checked BEFORE the team link — a family link is the more specific case. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const famEmail = params.get("fam");
    const famToken = params.get("tok");
    if (!famEmail || !famToken) return;

    window.history.replaceState({}, "", window.location.pathname);
    setMagicLoading(true);
    apiGet(`/family?email=${encodeURIComponent(famEmail)}&token=${encodeURIComponent(famToken)}`)
      .then((data) => {
        const snap = {
          screen: "family",
          selectedTeam: null,
          authData: null,
          playerName: "",
          family: { email: data.email, players: data.players },
        };
        applySnapshot(snap);
        // A deep entry point, like the team magic link — back should exit,
        // not walk into a chooser they never came from.
        window.history.replaceState({ nav: snap }, "");
        setMagicLoading(false);
      })
      .catch((e) => {
        setMagicError(e.message || "That link is invalid or has expired.");
        setMagicLoading(false);
      });
  }, []);

  /* Auto-login via magic link URL params */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamId = params.get("t");
    const code = params.get("code");
    if (teamId && code) {
      // Clear URL params without reload
      window.history.replaceState({}, "", window.location.pathname);
      setMagicLoading(true);
      apiPost("/auth", { teamId, passcode: code })
        .then((data) => {
          const snap = {
            screen: data.role === "coach" ? "coach" : "playerName",
            selectedTeam: null,
            authData: data,
            playerName: "",
          };
          applySnapshot(snap);
          // Magic links are a deep entry point — make this the base entry (back exits the app).
          window.history.replaceState({ nav: snap }, "");
          setMagicLoading(false);
        })
        .catch((e) => {
          setMagicError("Magic link failed: " + (e.message || "invalid link"));
          setMagicLoading(false);
        });
    }
  }, []);

  // Forward navigation — each pushes a history entry so the back button steps back through it.
  const handleTeamSelect = (team) => pushNav({ screen: "login", selectedTeam: team, authData: null, playerName: "" });
  const handleAdmin = () => pushNav({ screen: "admin", selectedTeam, authData, playerName });
  const handleLogin = (data) => pushNav({
    screen: data.role === "coach" ? "coach" : "playerName",
    selectedTeam, authData: data, playerName: "",
  });
  const handlePlayerName = (name) => pushNav({ screen: "tracker", selectedTeam, authData, playerName: name });

  // The family endpoint hands back a per-team parent token, so the existing
  // tracker screen works unchanged from here.
  const handleFamilySelect = (p) =>
    pushNav({
      screen: "tracker",
      selectedTeam: p.team,
      authData: { token: p.token, role: "parent", team: p.team },
      playerName: p.playerName,
      family,
    });

  // In-app "Back" buttons defer to the browser history so both paths behave identically.
  const goBack = () => window.history.back();

  // Logout is a hard reset — clear auth and replace the current entry so back can't re-enter.
  const handleLogout = () => {
    const snap = { screen: "teams", selectedTeam: null, authData: null, playerName: "", family: null };
    applySnapshot(snap);
    setMagicError("");
    window.history.replaceState({ nav: snap }, "");
  };

  if (magicLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white flex flex-col items-center justify-center p-4">
        <SartellLogo size="lg" />
        <p className="mt-6 text-gray-500 animate-pulse">Logging you in...</p>
      </div>
    );
  }

  switch (screen) {
    case "teams":
      // The team chooser is this app's entry point, so it carries the only way
      // back out to the platform launcher. Deeper screens have their own Back.
      return (
        <>
          <div className="bg-white/70 border-b border-slate-200">
            <div className="max-w-5xl mx-auto px-4 py-2">
              <div className="flex items-center justify-between gap-3">
                <Link to="/" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                  ← All Sartell Soccer apps
                </Link>
                <Link to="/help/availability" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                  Help
                </Link>
              </div>
            </div>
          </div>
          <TeamSelect onSelect={handleTeamSelect} onAdmin={handleAdmin} magicError={magicError} />
        </>
      );
    case "family":
      return (
        <FamilyScreen
          email={family?.email}
          players={family?.players || []}
          onSelect={handleFamilySelect}
          onLogout={handleLogout}
        />
      );
    case "admin":
      return <AdminPanel onBack={goBack} />;
    case "login":
      return <LoginScreen team={selectedTeam} onLogin={handleLogin} onBack={goBack} />;
    case "playerName":
      return <PlayerNameScreen team={authData.team} token={authData.token} onContinue={handlePlayerName} onBack={goBack} />;
    case "tracker":
      return <ParentTracker team={authData.team} token={authData.token} playerName={playerName} onBack={goBack} onLogout={handleLogout} />;
    case "coach":
      return <CoachView team={authData.team} token={authData.token} onLogout={handleLogout} />;
    default:
      return <TeamSelect onSelect={handleTeamSelect} />;
  }
}
